<template>
  <v-form
    ref="formCriteria"
    v-model="formValid"
    class="dynamic-trade-criteria-inputs"
    @submit.prevent
  >
    <v-row dense>
      <v-col
        v-for="filter in criteriaList"
        :key="filter.field.key"
        cols="8"
        :sm="filter.condition === 'between' || filter.field.input_type === 'date' ? 8 : 4"
      >
        <template v-if="isMasked(filter)">
          <div
            v-if="filter.condition === 'between'"
            class="app-align-items-center"
          >
            <BaseInput
              v-model="models[filter.field.key].start"
              v-money="getMask(filter)"
              :rules="getRules(filter)"
              :label="getLabel(filter, `inicial ${getSuffix(filter)}`)"
              :disabled="disabled"
              class="mr-3"
            />
            <BaseInput
              v-model="models[filter.field.key].end"
              v-money="getMask(filter)"
              :rules="getRules(filter)"
              :label="getLabel(filter, `final ${getSuffix(filter)}`)"
              :disabled="disabled"
            />
          </div>
          <BaseInput
            v-else
            v-model="models[filter.field.key].value"
            v-money="getMask(filter)"
            :rules="getRules(filter)"
            :label="getLabel(filter, getSuffix(filter))"
            :disabled="disabled"
          />
        </template>

        <template v-else-if="filter.field.input_type === 'date'">
          <p class="body-2 mb-1">
            {{ getLabel(filter) }}
          </p>
          <div class="app-align-items-center">
            <div class="mr-3">
              <BaseDatePicker
                name-placeholder="Data Inicial"
                :date-value="models[filter.field.key].start"
                :max="models[filter.field.key].end || null"
                :max-width="140"
                :disabled="disabled"
                locale="pt-br"
                time-zone="UTC"
                @on-input-date-picker="(value) => updateModel(filter, 'start', value)"
              />
            </div>
            <BaseDatePicker
              name-placeholder="Data Final"
              :date-value="models[filter.field.key].end"
              :max="null"
              :max-width="140"
              :disabled="disabled"
              locale="pt-br"
              time-zone="UTC"
              @on-input-date-picker="(value) => updateModel(filter, 'end', value)"
            />
          </div>
        </template>

        <template v-else-if="filter.field.input_type === 'select' || filter.field.input_type === 'multiselect'">
          <v-combobox
            v-if="filter.field.multi_valued == 'true'"
            v-model="models[filter.field.key].value"
            :items="optionsList[filter.field.key] || []"
            :label="getLabel(filter)"
            :disabled="disabled"
            item-text="name"
            item-value="value"
            multiple
            chips
            :rules="getRules(filter)"
          />
          <v-select
            v-else
            v-model="models[filter.field.key].value"
            :rules="getRules(filter)"
            :items="optionsList[filter.field.key] || []"
            :label="getLabel(filter)"
            :disabled="disabled"
            item-text="name"
            item-value="value"
            clearable
          />
        </template>

        <template v-else>
          <div
            v-if="filter.condition === 'between'"
            class="app-align-items-center"
          >
            <BaseInput
              v-model="models[filter.field.key].start"
              :rules="getRules(filter)"
              :label="getLabel(filter, 'inicial')"
              :disabled="disabled"
              class="mr-3"
            />
            <BaseInput
              v-model="models[filter.field.key].end"
              :rules="getRules(filter)"
              :label="getLabel(filter, 'final')"
              :disabled="disabled"
            />
          </div>
          <v-combobox
            v-else-if="filter.field.multi_valued == 'true'"
            v-model="models[filter.field.key].value"
            :items="[]"
            :label="getLabel(filter)"
            :disabled="disabled"
            hint="Para adicionar múltiplos valores aperte 'enter' após digitar."
            persistent-hint
            multiple
            chips
            :rules="getRules(filter)"
          />
          <BaseInput
            v-else
            v-model="models[filter.field.key].value"
            :rules="getRules(filter)"
            :label="getLabel(filter)"
            :disabled="disabled"
          />
        </template>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import BaseDatePicker from '../BaseDatePicker'
import { amountMask, rateMask } from '../../directives/moneyMasks'

export default {
  components: { BaseDatePicker },
  props: {
    scope: {
      type: String,
      default: 'criteria'
    },
    valuesValidation: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      criteriaList: [],
      models: {},
      optionsList: {},
      formValid: true
    }
  },
  computed: {
    ...mapState({
      suggestionList: (state) => state.programFilters.suggestions
    })
  },
  watch: {
    models: {
      handler() {
        this.$nextTick(this.emitFilters)
      },
      deep: true
    },
    formValid() {
      this.emitFilters()
    }
  },
  async created() {
    const result = await this.apiGetProgramFilters({ scope: this.scope, role: null })
    const list = result || []
    const models = {}
    list.forEach(filter => {
      models[filter.field.key] = this.getInitialModel(filter)
    })
    this.models = models
    this.criteriaList = list
    list.forEach(filter => this.loadOptions(filter))
  },
  methods: {
    ...mapActions('programFilters', ['apiGetProgramFilters', 'apiPostSuggestValues']),
    isMasked(filter) {
      return ['amount', 'percent'].includes(filter.field.input_type)
    },
    getMask(filter) {
      return filter.field.input_type === 'percent' ? rateMask : amountMask
    },
    isRequired(filter) {
      const raw = filter.field && filter.field.required !== undefined ? filter.field.required : filter.required
      return raw === true || String(raw).toLowerCase() === 'true'
    },
    getLabel(filter, complement = '') {
      const label = complement ? `${filter.name} ${complement}` : filter.name
      return this.isRequired(filter) ? `${label} *` : label
    },
    hasMissingRequired() {
      return this.criteriaList.some(filter => {
        if (!this.isRequired(filter)) return false
        const model = this.models[filter.field.key]
        if (!model) return true
        if (filter.condition === 'between' || filter.field.input_type === 'date') {
          return this.isEmpty(filter, model.start) || this.isEmpty(filter, model.end)
        }
        return this.isEmpty(filter, model.value)
      })
    },
    getSuffix(filter) {
      return filter.field.input_type === 'percent' ? '(%)' : '(R$)'
    },
    parseDefault(filter) {
      const raw = filter.default
      if (!raw) return null
      if (typeof raw !== 'string') return raw
      try {
        if (raw.startsWith('[') || raw.startsWith('{')) return JSON.parse(raw)
      } catch (error) {
        return null
      }
      return raw
    },
    formatDefaultForMask(filter, value) {
      if (value === null || value === undefined || value === '') return ''
      if (!this.isMasked(filter)) return value

      const number = filter.field.input_type === 'percent' ? parseFloat(value) * 100 : parseFloat(value)
      if (isNaN(number)) return ''

      const precision = filter.field.input_type === 'percent' ? rateMask.precision : amountMask.precision
      return number.toFixed(precision).replace('.', ',')
    },
    getInitialModel(filter) {
      const def = this.parseDefault(filter)
      const multi = filter.field.multi_valued == 'true'

      if (filter.condition === 'between' || filter.field.input_type === 'date') {
        const [start, end] = Array.isArray(def) ? def : [null, null]
        return {
          start: this.formatDefaultForMask(filter, start),
          end: this.formatDefaultForMask(filter, end)
        }
      }
      if (multi) {
        return { value: Array.isArray(def) ? def : (def ? [def] : []) }
      }
      const single = Array.isArray(def) ? def[0] : def
      return { value: typeof single === 'object' && single !== null ? '' : this.formatDefaultForMask(filter, single) }
    },
    async loadOptions(filter) {
      const { field } = filter
      if (!['select', 'multiselect'].includes(field.input_type)) return
      try {
        if (field.datasource && field.datasource.startsWith('[')) {
          this.$set(this.optionsList, field.key, JSON.parse(field.datasource))
        } else {
          await this.apiPostSuggestValues({ category: this.scope, key: field.key })
          this.$set(this.optionsList, field.key, [...(this.suggestionList || [])])
        }
      } catch (error) {
        this.$set(this.optionsList, field.key, [])
      }
    },
    updateModel(filter, prop, value) {
      this.$set(this.models[filter.field.key], prop, value)
    },
    isEmpty(filter, value) {
      if (value === null || value === undefined || value === '') return true
      if (Array.isArray(value)) return value.length === 0
      if (this.isMasked(filter)) {
        const clean = String(value).replace(/\s%/, '')
        return this.parseNumberDecimal(clean) === 0
      }
      return false
    },
    getRules(filter) {
      const validation = filter.field && filter.field.validation
      return [
        (value) => {
          if (this.isEmpty(filter, value)) {
            return this.isRequired(filter) ? 'Campo obrigatório' : true
          }
          if (validation) {
            const input = value
            const values = this.valuesValidation
            const filters = this.buildFilters()
            try {
              return eval(validation)
            } catch (error) {
              return true
            }
          }
          if (filter.field.input_type === 'percent') return this.isValidLengthForTax(value)
          if (filter.field.input_type === 'amount') return this.parseNumberDecimal(value) > 0 || 'Valor inválido'
          return true
        }
      ]
    },
    normalizeValue(filter, value) {
      if (filter.field.input_type === 'amount') {
        return this.parseNumberDecimal(value).toFixed(2)
      }
      if (filter.field.input_type === 'percent') {
        return this.convertToDecimal(this.parseNumberDecimal(String(value).replace(/\s%/, ''))).toFixed(6)
      }
      if (typeof value === 'object' && value !== null && value.name) {
        return value.name
      }
      if (typeof value === 'string' && !['select', 'multiselect'].includes(filter.field.input_type)) {
        return value.toUpperCase()
      }
      return value
    },
    buildFilters() {
      const filters = []
      this.criteriaList.forEach(filter => {
        const model = this.models[filter.field.key]
        if (!model) return
        let values = []
        if (filter.condition === 'between' || filter.field.input_type === 'date') {
          if (this.isEmpty(filter, model.start) || this.isEmpty(filter, model.end)) return
          values = [model.start, model.end]
        } else {
          if (this.isEmpty(filter, model.value)) return
          values = Array.isArray(model.value) ? model.value : [model.value]
        }
        filters.push({
          key: filter.field.key,
          condition: filter.condition,
          type: filter.field.type,
          input_type: filter.field.input_type,
          multi_valued: filter.field.multi_valued,
          name: filter.name,
          values: values.map(value => this.normalizeValue(filter, value))
        })
      })
      return filters
    },
    emitFilters() {
      const isValid = this.formValid && !this.hasMissingRequired()
      this.$emit('executeActionFilter', {
        filters: isValid ? this.buildFilters() : [],
        category: this.scope
      })
    }
  }
}
</script>
